// src/pages/Dashboard/hooks/useDashboardChat.js
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { DASHBOARD_SYSTEM_PROMPT } from '../utils/dashboardConstants';
import { processarAnalise } from '../utils/analysisProcessor';
import { getDadosCliente, prepararContextoCasoParaChat } from '../utils/clientDataProcessor';

export const useDashboardChat = (casoSelecionado, jurisprudenciasRelacionadas = []) => {
  const { user } = useAuth();
  const messagesEndRef = useRef(null);

  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchModels = useCallback(async () => {
    try {
      const response = await axios.get('/api/chat/models');
      if (response.data.success) {
        setModels(response.data.data);
        if (response.data.data.length > 0) {
          const gptModel = response.data.data.find(m => m.id.includes('gpt-4')) || response.data.data[0];
          setSelectedModel(gptModel);
          console.log('✅ Modelos carregados, modelo padrão:', gptModel.id);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao buscar modelos:', error);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const prepararContexto = useCallback(() => {
    if (!casoSelecionado) return '';

    const analise = processarAnalise(casoSelecionado);
    const dadosClienteStr = getDadosCliente(casoSelecionado, false);
    
    return prepararContextoCasoParaChat(
      casoSelecionado,
      analise,
      dadosClienteStr,
      jurisprudenciasRelacionadas,
      user
    );
  }, [casoSelecionado, jurisprudenciasRelacionadas, user]);

  // ✅ FUNÇÃO DE STREAMING - CORRIGIDA
  const sendStreamMessage = useCallback(async (message = null) => {
    const messageToSend = message || inputMessage;
    
    if (!messageToSend.trim() || !selectedModel || isLoading) {
      console.log('❌ Condições não atendidas:', { messageToSend, selectedModel, isLoading });
      return;
    }

    console.log('🚀 Iniciando envio com streaming...');

    const userMessage = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    };

    const assistantPlaceholder = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      model: selectedModel.id,
      usage: {}
    };

    // Capturar histórico ANTES de atualizar estado
    const messageHistory = [...messages];

    // Atualizar UI com mensagem do usuário e placeholder
    setMessages(prev => [...prev, userMessage, assistantPlaceholder]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const contextContent = prepararContexto();
      
      console.log('📡 Fazendo requisição de streaming...');

      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          model: selectedModel.id,
          messages: [...messageHistory, userMessage],
          systemPrompt: DASHBOARD_SYSTEM_PROMPT,
          contextContent: contextContent || undefined
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream não suportado');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      console.log('📖 Iniciando leitura do stream...');

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('✅ Stream finalizado');
          break;
        }

        // Decodificar chunk
        buffer += decoder.decode(value, { stream: true });
        
        // Processar linhas completas
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Última linha pode estar incompleta

        for (const line of lines) {
          const trimmedLine = line.trim();
          
          // Ignorar linhas vazias ou que não sejam SSE
          if (!trimmedLine || !trimmedLine.startsWith('data:')) {
            continue;
          }

          try {
            // Extrair JSON após "data: "
            const jsonStr = trimmedLine.substring(5).trim();
            
            if (!jsonStr) continue;

            const parsed = JSON.parse(jsonStr);

            // Verificar se há erro
            if (parsed.error) {
              throw new Error(parsed.error);
            }

            // Adicionar conteúdo à mensagem do assistente
            if (parsed.content) {
              setMessages(prev => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                
                if (lastIndex >= 0 && newMessages[lastIndex].role === 'assistant') {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    content: newMessages[lastIndex].content + parsed.content
                  };
                }
                
                return newMessages;
              });
            }
          } catch (parseError) {
            console.warn('⚠️ Erro ao parsear linha SSE:', parseError.message, '| Linha:', trimmedLine);
          }
        }
      }

    } catch (error) {
      console.error('❌ Erro no streaming:', error);
      
      setMessages(prev => {
        const newMessages = [...prev];
        const lastIndex = newMessages.length - 1;
        
        if (lastIndex >= 0 && newMessages[lastIndex].role === 'assistant') {
          newMessages[lastIndex] = {
            ...newMessages[lastIndex],
            content: `❌ Erro ao gerar resposta: ${error.message}`,
            error: true
          };
        }
        
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, selectedModel, isLoading, messages, prepararContexto]);

  // Método antigo (sem streaming) - manter para fallback
  const sendMessage = useCallback(async (message = null) => {
    const messageToSend = message || inputMessage;
    
    if (!messageToSend.trim() || !selectedModel || isLoading) {
      return;
    }

    const userMessage = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const contextContent = prepararContexto();

      const response = await axios.post('/api/chat/completions', {
        model: selectedModel.id,
        messages: [...messages, userMessage],
        systemPrompt: DASHBOARD_SYSTEM_PROMPT,
        contextContent: contextContent || undefined
      });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.data.content,
          timestamp: new Date().toISOString(),
          model: selectedModel.id,
          usage: response.data.data.usage
        };
        
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem.',
        timestamp: new Date().toISOString(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, selectedModel, isLoading, messages, prepararContexto]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    console.log('✅ Mensagens do chat limpas');
  }, []);

  const selectModel = useCallback((model) => {
    setSelectedModel(model);
    console.log('✅ Modelo selecionado:', model.id);
  }, []);

  const updateInputMessage = useCallback((value) => {
    setInputMessage(value);
  }, []);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (casoSelecionado?.id) {
      clearMessages();
    }
  }, [casoSelecionado?.id, clearMessages]);

  return {
    models,
    selectedModel,
    messages,
    inputMessage,
    isLoading,
    messagesEndRef,
    sendMessage,
    sendStreamMessage,
    clearMessages,
    selectModel,
    updateInputMessage,
    scrollToBottom
  };
};